import { useEffect, useMemo, useState } from "react";
import { Card, SectionHeader } from "../components/Card";
import { Button } from "../components/Button";
import { useAuth } from "../lib/auth.jsx";

function money(cents, currency = "USD") {
  const code = String(currency || "USD").toUpperCase();
  const amount = (Number(cents || 0) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${code} ${amount}`;
}

function statusLabel(status = "") {
  return String(status || "pending").replace(/_/g, " ");
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function isPayable(status = "") {
  return ["pending", "issued"].includes(String(status).toLowerCase());
}

export function InvoicesScreen() {
  const { authedFetch, profile } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [err, setErr] = useState("");

  const isAdmin = profile?.role === "admin_tech";
  const totals = useMemo(() => {
    const open = invoices.filter((inv) => isPayable(inv.status)).reduce((sum, inv) => sum + Number(inv.total_cents || 0), 0);
    const paid = invoices.filter((inv) => String(inv.status).toLowerCase() === "paid").length;
    return { open, paid };
  }, [invoices]);

  async function refresh() {
    setLoading(true);
    setErr("");
    try {
      const out = await authedFetch(isAdmin ? "/api/invoices" : "/api/invoices/mine");
      setInvoices(out.invoices || []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, [isAdmin]);

  async function patchInvoice(invoice, patch) {
    setBusyId(invoice.id);
    setErr("");
    try {
      const out = await authedFetch(`/api/invoices/${invoice.id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      setInvoices((rows) => rows.map((row) => row.id === invoice.id ? { ...row, ...(out.invoice || {}) } : row));
      setSelected((current) => current?.id === invoice.id ? { ...current, ...(out.invoice || {}) } : current);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusyId("");
    }
  }

  async function declarePaid(invoice) {
    setBusyId(invoice.id);
    setErr("");
    try {
      const out = await authedFetch(`/api/invoices/${invoice.id}/declare-paid`, { method: "POST" });
      setInvoices((rows) => rows.map((row) => row.id === invoice.id ? { ...row, ...(out.invoice || {}) } : row));
      setSelected((current) => current?.id === invoice.id ? { ...current, ...(out.invoice || {}) } : current);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusyId("");
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", paddingBottom: 80 }}>
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "30px 16px" }}>
        <SectionHeader
          title="Invoices"
          subtitle={isAdmin ? "Create, review, and settle client invoices." : "Review your studio payment records and declare cash or transfer payments."}
        />

        {err && <div role="alert" style={alertStyle()}>{err}</div>}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))", gap: 12, marginBottom: 18 }}>
          <Metric label="Invoices" value={invoices.length} />
          <Metric label="Paid" value={totals.paid} />
          <Metric label="Open total" value={money(totals.open, invoices[0]?.currency || "USD")} />
        </div>

        <Card style={{ maxWidth: "none", padding: 0, overflow: "hidden" }}>
          <div style={{ padding: 18, display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", borderBottom: "1px solid var(--border)" }}>
            <div>
              <h3 style={{ margin: 0, color: "var(--text)", fontFamily: "var(--font-display)", fontSize: 22 }}>Invoice list</h3>
              <p style={{ margin: "4px 0 0", color: "var(--text-muted)", fontSize: 13 }}>
                {isAdmin ? "Manual invoice creation remains in Admin > Invoices." : "Open invoices can be declared paid after cash or transfer."}
              </p>
            </div>
            <Button size="sm" variant="outline" label="Refresh" onClick={refresh} loading={loading} />
          </div>

          {loading ? (
            <div style={emptyStyle()}>Loading invoices...</div>
          ) : invoices.length === 0 ? (
            <div style={emptyStyle()}>No invoices yet.</div>
          ) : (
            <div style={{ display: "grid" }}>
              {invoices.map((invoice) => (
                <button
                  key={invoice.id}
                  type="button"
                  onClick={() => setSelected(invoice)}
                  style={rowButtonStyle()}
                >
                  <span style={{ minWidth: 0 }}>
                    <strong style={{ display: "block", color: "var(--text)", fontSize: 14 }}>
                      {invoice.number || `Invoice ${invoice.id.slice(0, 8)}`}
                    </strong>
	                    <span style={{ display: "block", color: "var(--text-muted)", fontSize: 12, marginTop: 4 }}>
	                      {isAdmin ? invoice.customer?.full_name || invoice.customer?.email || "Customer" : "Oge Studio"}
	                    </span>
	                    <span style={{ display: "block", color: "var(--text-muted)", fontSize: 12, marginTop: 3 }}>
	                      Issued {formatDateTime(invoice.created_at) || "date unavailable"}
	                      {invoice.booking?.slot?.starts_at ? ` | Appointment ${formatDateTime(invoice.booking.slot.starts_at)}` : ""}
	                    </span>
	                  </span>
                  <span style={{ display: "grid", gap: 5, justifyItems: "end" }}>
                    <strong style={{ color: "var(--text)", fontSize: 15 }}>{money(invoice.total_cents, invoice.currency)}</strong>
                    <StatusPill status={invoice.status} />
                  </span>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>

      {selected && (
        <InvoiceDialog
          invoice={selected}
          isAdmin={isAdmin}
          busy={busyId === selected.id}
          onClose={() => setSelected(null)}
          onDeclarePaid={() => declarePaid(selected)}
          onMarkPaid={() => patchInvoice(selected, { status: "paid" })}
          onVoid={() => patchInvoice(selected, { status: "void" })}
        />
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <Card style={{ maxWidth: "none", padding: 18 }}>
      <div style={{ color: "var(--text-muted)", fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</div>
      <div style={{ color: "var(--text)", fontSize: 26, fontWeight: 900, marginTop: 6 }}>{value}</div>
    </Card>
  );
}

function InvoiceDialog({ invoice, isAdmin, busy, onClose, onDeclarePaid, onMarkPaid, onVoid }) {
  const items = Array.isArray(invoice.items) ? invoice.items : [];
  return (
    <div role="dialog" aria-modal="true" aria-label="Invoice details" style={modalBackdropStyle()}>
      <Card style={{ width: "min(720px, calc(100vw - 28px))", maxHeight: "calc(100svh - 32px)", overflow: "auto", padding: 0 }}>
        <div style={{ padding: 20, borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, color: "var(--text)", fontFamily: "var(--font-display)", fontSize: 24 }}>
              {invoice.number || "Invoice"}
            </h3>
            <div style={{ marginTop: 6 }}><StatusPill status={invoice.status} /></div>
          </div>
          <Button size="sm" variant="neutral" label="Close" onClick={onClose} />
        </div>

        <div style={{ padding: 20, display: "grid", gap: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
            <Info label="Total" value={money(invoice.total_cents, invoice.currency)} />
            <Info label="Method" value={statusLabel(invoice.method || "not set")} />
	            <Info label="Issued" value={formatDateTime(invoice.created_at) || "-"} />
	            {invoice.paid_at && <Info label="Paid" value={formatDateTime(invoice.paid_at)} />}
	            <Info label="Booking" value={invoice.booking_id ? invoice.booking_id.slice(0, 8).toUpperCase() : "-"} />
          </div>

          <div style={{ border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden" }}>
            {(items.length ? items : [{ description: invoice.notes || "Studio service", quantity: 1, amount_cents: invoice.total_cents }]).map((item, index) => (
              <div key={item.id || index} style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 12,
                padding: 14,
                borderTop: index ? "1px solid var(--border)" : "none",
              }}>
                <span style={{ color: "var(--text)", fontWeight: 700 }}>{item.description || "Item"}</span>
                <span style={{ color: "var(--text)", fontWeight: 900 }}>{money(item.amount_cents ?? (Number(item.quantity || 1) * Number(item.unit_cents || 0)), invoice.currency)}</span>
              </div>
            ))}
          </div>

          {invoice.notes && (
            <div style={{ color: "var(--text-muted)", fontSize: 13, lineHeight: 1.55 }}>{invoice.notes}</div>
          )}

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {!isAdmin && isPayable(invoice.status) && (
              <Button label="Declare paid" variant="primary" onClick={onDeclarePaid} loading={busy} />
            )}
            {isAdmin && isPayable(invoice.status) && (
              <Button label="Mark paid" variant="primary" onClick={onMarkPaid} loading={busy} />
            )}
            {isAdmin && String(invoice.status).toLowerCase() !== "void" && (
              <Button label="Void" variant="danger" onClick={onVoid} disabled={busy} />
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div style={{ padding: 14, borderRadius: 14, background: "var(--bg-soft)", border: "1px solid var(--border)" }}>
      <div style={{ color: "var(--text-muted)", fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</div>
      <div style={{ color: "var(--text)", fontWeight: 900, marginTop: 5 }}>{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const normalized = String(status || "pending").toLowerCase();
  const paid = normalized === "paid";
  const voided = normalized === "void" || normalized === "voided";
  return (
    <span style={{
      display: "inline-flex",
      padding: "5px 10px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 900,
      color: paid ? "#111" : voided ? "#B42318" : "var(--text)",
      background: paid ? "var(--gold)" : voided ? "rgba(180, 50, 50, 0.10)" : "var(--bg-soft)",
      border: "1px solid var(--border)",
      textTransform: "capitalize",
    }}>
      {statusLabel(status)}
    </span>
  );
}

function rowButtonStyle() {
  return {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: 14,
    alignItems: "center",
    textAlign: "left",
    padding: "16px 18px",
    border: "none",
    borderBottom: "1px solid var(--border)",
    background: "transparent",
    cursor: "pointer",
    minWidth: 0,
  };
}

function alertStyle() {
  return {
    background: "rgba(180, 50, 50, 0.10)",
    color: "#B42318",
    border: "1px solid rgba(180, 50, 50, 0.22)",
    borderRadius: 14,
    padding: 12,
    marginBottom: 16,
    fontWeight: 700,
  };
}

function emptyStyle() {
  return { padding: 32, textAlign: "center", color: "var(--text-muted)" };
}

function modalBackdropStyle() {
  return {
    position: "fixed",
    inset: 0,
    zIndex: 80,
    background: "rgba(13, 8, 16, 0.62)",
    display: "grid",
    placeItems: "center",
    padding: 14,
  };
}
